import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { addEquipmentExpense } from '../../../../redux/Slices/EquipmentExpensesSlice';
import { getBanks, getSafes, addProject, getCustomers, getTaxes, getCostCenters, addCustomer, addTax, addCostCenter } from '../../../../redux/Slices/authSlice';
import { getEquipments, addEquipment } from '../../../../redux/Slices/EquipmentSlice';
import { getProjects } from '../../../../redux/Slices/ProjectsSlice';
import axios from 'axios';
import { BASE_URL, getToken } from '../../../../shared/utils.js/utils';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

// add equipment expenses 
const AddEquipmentExpenses = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { banks, banksPagination, safes, safesPagination, customers, customersPagination, taxes, taxesPagination, costCenters, costCentersPagination } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isRTL = i18n.language === 'ar';
  const [form, setForm] = useState({
    equipment_id: '',
    expense_date: '',
    amount: '',
    description_ar: '',
    description_en: '',
    payment_account_type: '', // 'bank' or 'safe'
    payment_account_id: '',
    project_id: '',
    notes: '',
  });

  // Equipment dropdown
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentPagination, setEquipmentPagination] = useState(null);
  const equipmentSearchInputRef = useRef(null);

  // Project dropdown
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPagination, setProjectPagination] = useState(null);
  const projectSearchInputRef = useRef(null);

  // Bank/Safe dropdown
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [accountPage, setAccountPage] = useState(1);
  const accountSearchInputRef = useRef(null);
  const accountSearchTimeoutRef = useRef(null);

  // Modals state
  const [showAddEquipmentModal, setShowAddEquipmentModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [isSubmittingEquipment, setIsSubmittingEquipment] = useState(false);
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);
  const [isSubmittingTax, setIsSubmittingTax] = useState(false);
  const [isSubmittingCostCenter, setIsSubmittingCostCenter] = useState(false);

  // Equipment modal form
  const [equipmentForm, setEquipmentForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
    is_active: true,
    notes: '',
  });

  // Project modal form
  const [projectForm, setProjectForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });

  // Project modal dropdown states
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Customer modal form
  const [customerForm, setCustomerForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: '',
  });

  // Tax modal form
  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });

  // Cost Center modal form
  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });

  const fetchEquipments = useCallback(
    async (page = equipmentPage, search = equipmentSearchTerm) => {
      try {
        const token = getToken();
        if (!token) throw new Error(t('equipmentExpenses.messages.noAuthToken'));
        const response = await axios.get(`${BASE_URL}/equipment`, {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(response.data);
        setEquipmentOptions(items);
        setEquipmentPagination(paginationMeta);
      } catch (error) {
        toast.error(error?.response?.data?.message || t('equipmentExpenses.messages.fetchEquipmentsError'), { rtl: isRTL });
      }
    },
    [t, isRTL]
  );

  const fetchProjects = useCallback(
    async (page = projectPage, search = projectSearchTerm) => {
      try {
        const token = getToken();
        if (!token) throw new Error(t('equipmentExpenses.messages.noAuthToken'));
        const response = await axios.get(`${BASE_URL}/projects`, {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(response.data);
        setProjectOptions(items);
        setProjectPagination(paginationMeta);
      } catch (error) {
        toast.error(error?.response?.data?.message || t('equipmentExpenses.messages.fetchProjectsError'), { rtl: isRTL });
      }
    },
    [t, isRTL]
  );

  useEffect(() => {
    if (equipmentDropdownOpen) {
      fetchEquipments(equipmentPage, equipmentSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentDropdownOpen, equipmentPage, equipmentSearchTerm]);

  useEffect(() => {
    if (projectDropdownOpen) {
      fetchProjects(projectPage, projectSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDropdownOpen, projectPage, projectSearchTerm]);

  useEffect(() => {
    if (equipmentSearchTerm !== undefined && equipmentDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEquipmentPage(1);
        fetchEquipments(1, equipmentSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentSearchTerm, equipmentDropdownOpen]);

  useEffect(() => {
    if (projectSearchTerm !== undefined && projectDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setProjectPage(1);
        fetchProjects(1, projectSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSearchTerm, projectDropdownOpen]);

  // Fetch banks or safes based on payment_account_type
  useEffect(() => {
    if (accountDropdownOpen && form.payment_account_type) {
      if (form.payment_account_type === 'bank') {
        dispatch(getBanks({ page: accountPage, per_page: 10, search: accountSearchTerm || undefined }));
      } else if (form.payment_account_type === 'safe') {
        dispatch(getSafes({ page: accountPage, per_page: 10, search: accountSearchTerm || undefined }));
      }
    }
  }, [accountDropdownOpen, form.payment_account_type, accountPage, accountSearchTerm, dispatch]);

  // Search banks/safes with debounce
  useEffect(() => {
    if (accountSearchTerm !== undefined && accountDropdownOpen && form.payment_account_type) {
      if (accountSearchTimeoutRef.current) clearTimeout(accountSearchTimeoutRef.current);
      accountSearchTimeoutRef.current = setTimeout(() => {
        setAccountPage(1);
        if (form.payment_account_type === 'bank') {
          dispatch(getBanks({ page: 1, per_page: 10, search: accountSearchTerm || undefined }));
        } else if (form.payment_account_type === 'safe') {
          dispatch(getSafes({ page: 1, per_page: 10, search: accountSearchTerm || undefined }));
        }
      }, 300);
    }
    return () => {
      if (accountSearchTimeoutRef.current) clearTimeout(accountSearchTimeoutRef.current);
    };
  }, [accountSearchTerm, accountDropdownOpen, form.payment_account_type, dispatch]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownContainer = target.closest('[data-dropdown-container]');
      const isDropdownToggle = target.closest('[data-dropdown-toggle]');
      const isDropdownMenu = target.closest('[data-dropdown-menu]');
      const isModal = target.closest('[data-modal]');

      // Don't close dropdowns if a modal is open
      if (showAddEquipmentModal || showAddProjectModal || showAddCustomerModal || showAddTaxModal || showAddCostCenterModal) {
        return;
      }

      if (!isDropdownContainer && !isDropdownToggle && !isDropdownMenu && !isModal) {
        if (equipmentDropdownOpen) setEquipmentDropdownOpen(false);
        if (projectDropdownOpen) setProjectDropdownOpen(false);
        if (accountDropdownOpen) setAccountDropdownOpen(false);
        if (customerDropdownOpen) setCustomerDropdownOpen(false);
        if (taxDropdownOpen) setTaxDropdownOpen(false);
        if (costCenterDropdownOpen) setCostCenterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [equipmentDropdownOpen, projectDropdownOpen, accountDropdownOpen, customerDropdownOpen, taxDropdownOpen, costCenterDropdownOpen, showAddEquipmentModal, showAddProjectModal, showAddCustomerModal, showAddTaxModal, showAddCostCenterModal]);

  // Project modal dropdowns useEffect hooks
  useEffect(() => {
    if (customerDropdownOpen) {
      dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined }));
    }
  }, [customerDropdownOpen, customerCurrentPage, customerSearchTerm, dispatch]);

  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
    }
  }, [taxDropdownOpen, taxCurrentPage, taxSearchTerm, dispatch]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
    }
  }, [costCenterDropdownOpen, costCenterCurrentPage, costCenterSearchTerm, dispatch]);

  // Search with debounce for project modal dropdowns
  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
    };
  }, [customerSearchTerm, customerDropdownOpen, dispatch]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
    };
  }, [taxSearchTerm, taxDropdownOpen, dispatch]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
    };
  }, [costCenterSearchTerm, costCenterDropdownOpen, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'payment_account_type') {
      setForm((prev) => ({
        ...prev,
        payment_account_type: value,
        payment_account_id: '', // Reset when type changes
      }));
      setAccountSearchTerm('');
      setAccountPage(1);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEquipmentFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEquipmentForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTaxFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCostCenterFormChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    if (!equipmentForm.name_ar || !equipmentForm.name_en) {
      toast.error(t('equipmentExpenses.modals.addEquipment.requiredFields'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingEquipment(true);
      const result = await dispatch(addEquipment(equipmentForm)).unwrap();
      if (result?.data?.id) {
        // Refresh equipments list
        await fetchEquipments(equipmentPage, equipmentSearchTerm);
        // Set the newly added equipment
        setForm((prev) => ({ ...prev, equipment_id: String(result.data.id) }));
        setShowAddEquipmentModal(false);
        setEquipmentForm({
          name_ar: '',
          name_en: '',
          code: '',
          is_active: true,
          notes: '',
        });
        toast.success(t('equipmentExpenses.modals.addEquipment.success'), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error || t('equipmentExpenses.modals.addEquipment.error'), { rtl: isRTL });
    } finally {
      setIsSubmittingEquipment(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!projectForm.name_ar) {
      toast.error(t('equipmentExpenses.modals.addProject.requiredFields'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingProject(true);
      const payload = {
        code: projectForm.code,
        name_ar: projectForm.name_ar,
        name_en: projectForm.name_en || undefined,
        customer_id: projectForm.customer_id || null,
        start_date: projectForm.start_date || undefined,
        end_date: projectForm.end_date || undefined,
        status: projectForm.status,
        contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
        tax_id: projectForm.tax_id || null,
        cost_center_id: projectForm.cost_center_id || null,
        notes: projectForm.notes || undefined,
      };
      const result = await dispatch(addProject(payload)).unwrap();
      if (result?.data?.id) {
        // Refresh projects list
        await fetchProjects(projectPage, projectSearchTerm);
        // Set the newly added project
        setForm((prev) => ({ ...prev, project_id: String(result.data.id) }));
        setShowAddProjectModal(false);
        setProjectForm({
          code: '',
          name_ar: '',
          name_en: '',
          customer_id: null,
          start_date: '',
          end_date: '',
          status: 'planned',
          contract_value: '',
          tax_id: null,
          cost_center_id: null,
          notes: '',
        });
        toast.success(t('equipmentExpenses.modals.addProject.success'), { rtl: isRTL });
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('equipmentExpenses.modals.addProject.error', { defaultValue: 'فشل إضافة المشروع' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.name_ar || !customerForm.name_en) {
      toast.error(t('quotations.add.modals.addCustomer.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية والإنجليزية' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCustomer(true);
      const result = await dispatch(addCustomer(customerForm)).unwrap();
      if (result?.data?.id) {
        // Refresh customers list
        await dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined }));
        // Set the newly added customer
        setProjectForm((prev) => ({ ...prev, customer_id: result.data.id }));
        setShowAddCustomerModal(false);
        setCustomerForm({
          name_ar: '',
          name_en: '',
          code: '',
          contact_person: '',
          email: '',
          phone: '',
          address: '',
          tax_number: '',
          is_active: true,
          notes: '',
        });
        toast.success(t('quotations.add.modals.addCustomer.success', { defaultValue: 'تم إضافة العميل بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('quotations.add.modals.addCustomer.error', { defaultValue: 'فشل إضافة العميل' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingCustomer(false);
    }
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    if (!taxForm.name_ar || !taxForm.name_en || !taxForm.rate) {
      toast.error(t('quotations.add.modals.addTax.requiredFields', { defaultValue: 'برجاء إدخال جميع الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingTax(true);
      const result = await dispatch(addTax(taxForm)).unwrap();
      if (result?.data?.id) {
        // Refresh taxes list
        await dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
        // Set the newly added tax
        setProjectForm((prev) => ({ ...prev, tax_id: result.data.id }));
        setShowAddTaxModal(false);
        setTaxForm({
          name_ar: '',
          name_en: '',
          rate: '',
          is_active: true,
        });
        toast.success(t('quotations.add.modals.addTax.success', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      // Extract error message from response
      let errorMessage = t('quotations.add.modals.addTax.error', { defaultValue: 'فشل إضافة الضريبة' });
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors) {
          // Handle validation errors
          const errorMessages = Object.values(errorData.errors).flat();
          errorMessage = errorMessages.join(', ') || errorMessage;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingTax(false);
    }
  };

  const handleAddCostCenter = async (e) => {
    e.preventDefault();
    if (!costCenterForm.name_ar || !costCenterForm.name_en) {
      toast.error(t('quotations.add.modals.addCostCenter.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية والإنجليزية' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCostCenter(true);
      const result = await dispatch(addCostCenter(costCenterForm)).unwrap();
      if (result?.data?.id) {
        // Refresh cost centers list
        await dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
        // Set the newly added cost center
        setProjectForm((prev) => ({ ...prev, cost_center_id: result.data.id }));
        setShowAddCostCenterModal(false);
        setCostCenterForm({
          name_ar: '',
          name_en: '',
          code: '',
        });
        toast.success(t('quotations.add.modals.addCostCenter.success', { defaultValue: 'تم إضافة مركز التكلفة بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('quotations.add.modals.addCostCenter.error', { defaultValue: 'فشل إضافة مركز التكلفة' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingCostCenter(false);
    }
  };

  const renderDropdownItem = (item, codeKey, nameArKey, nameEnKey) => {
    const code = item[codeKey];
    const nameAr = item[nameArKey];
    const nameEn = item[nameEnKey];
    const displayName = isRTL ? (nameAr || nameEn || '') : (nameEn || nameAr || '');
    return code ? `${code} - ${displayName}` : displayName;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.equipment_id) {
      toast.error(t('equipmentExpenses.add.messages.equipmentRequired'), { rtl: isRTL });
      return;
    }
    if (!form.expense_date || !form.amount || !form.description_ar || !form.description_en) {
      toast.error(t('equipmentExpenses.add.messages.fieldsRequired'), { rtl: isRTL });
      return;
    }
    if (!form.payment_account_type || !form.payment_account_id) {
      toast.error(t('equipmentExpenses.add.messages.paymentAccountRequired'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmitting(true);
      await dispatch(
        addEquipmentExpense({
          equipmentId: form.equipment_id,
          expenseData: {
            expense_date: form.expense_date,
            amount: Number(form.amount),
            description_ar: form.description_ar,
            description_en: form.description_en,
            payment_account_id: Number(form.payment_account_id),
            project_id: form.project_id ? Number(form.project_id) : null,
            notes: form.notes || null,
          },
        })
      ).unwrap();
      toast.success(t('equipmentExpenses.add.messages.addSuccess'), { rtl: isRTL });
      navigate('/equipment-expenses');
    } catch (error) {
      toast.error(error || t('equipmentExpenses.add.messages.addError'), { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAccountOptions = () => {
    if (form.payment_account_type === 'bank') {
      return Array.isArray(banks) ? banks : [];
    } else if (form.payment_account_type === 'safe') {
      return Array.isArray(safes) ? safes : [];
    }
    return [];
  };

  const getAccountPagination = () => {
    if (form.payment_account_type === 'bank') {
      return banksPagination || { current_page: 1, last_page: 1 };
    } else if (form.payment_account_type === 'safe') {
      return safesPagination || { current_page: 1, last_page: 1 };
    }
    return { current_page: 1, last_page: 1 };
  };

  const getSelectedAccount = () => {
    if (!form.payment_account_id) return null;
    const options = getAccountOptions();
    return options.find((item) => String(item.id) === String(form.payment_account_id));
  };

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <h2 style={{ marginBottom: 20, fontSize: '20px' }}>{t('equipmentExpenses.add.title')}</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('equipmentExpenses.add.equipment')}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  data-dropdown-toggle
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEquipmentDropdownOpen((prev) => !prev);
                  }}
                  style={{ flex: 1, ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                >
                  <span>
                    {form.equipment_id
                      ? (isRTL
                        ? equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_ar ||
                        equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_en ||
                        `${t('equipmentExpenses.tableHeaders.equipment')} #${form.equipment_id}`
                        : equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_en ||
                        equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_ar ||
                        `${t('equipmentExpenses.tableHeaders.equipment')} #${form.equipment_id}`)
                      : t('equipmentExpenses.add.selectEquipment')}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowAddEquipmentModal(true); }}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '44px',
                    height: '44px'
                  }}
                  title={t('equipmentExpenses.modals.addEquipment.title')}
                >
                  <FaPlus />
                </button>
              </div>
              <DropdownWithSearch
                isOpen={equipmentDropdownOpen}
                searchTerm={equipmentSearchTerm}
                onSearchChange={setEquipmentSearchTerm}
                searchInputRef={equipmentSearchInputRef}
                items={equipmentOptions}
                pagination={equipmentPagination}
                onPageChange={setEquipmentPage}
                onClose={() => setEquipmentDropdownOpen(false)}
                onSelect={(item) => {
                  setForm((prev) => ({ ...prev, equipment_id: String(item.id) }));
                  setEquipmentDropdownOpen(false);
                  setEquipmentSearchTerm('');
                  setEquipmentPage(1);
                }}
                label={t('equipmentExpenses.tableHeaders.equipment')}
              />
            </label>
          </div>

          <div style={gridStyle}>
            <Input label={t('equipmentExpenses.add.expenseDate')} type="date" name="expense_date" value={form.expense_date} onChange={handleChange} required isRTL={isRTL} />
            <Input label={t('equipmentExpenses.add.amount')} type="number" min="0"  name="amount" value={form.amount} onChange={handleChange} required isRTL={isRTL} />
          </div>

          <div style={gridStyle}>
            {isRTL ? (
              <>
                <Input label={t('equipmentExpenses.add.descriptionAr')} name="description_ar" value={form.description_ar} onChange={handleChange} required isRTL={true} />
                <Input label={t('equipmentExpenses.add.descriptionEn')} name="description_en" value={form.description_en} onChange={handleChange} required isRTL={false} />
              </>
            ) : (
              <>
                <Input label={t('equipmentExpenses.add.descriptionEn')} name="description_en" value={form.description_en} onChange={handleChange} required isRTL={false} />
                <Input label={t('equipmentExpenses.add.descriptionAr')} name="description_ar" value={form.description_ar} onChange={handleChange} required isRTL={true} />
              </>
            )}
          </div>

          <div style={{ position: 'relative' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('equipmentExpenses.add.paymentAccountType')}</span>
              <select
                name="payment_account_type"
                value={form.payment_account_type}
                onChange={handleChange}
                style={{ ...filterInputStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                required
              >
                <option value="">{t('equipmentExpenses.add.selectPaymentAccountType')}</option>
                <option value="bank">{t('equipmentExpenses.add.bank')}</option>
                <option value="safe">{t('equipmentExpenses.add.safe')}</option>
              </select>
            </label>
          </div>

          {form.payment_account_type && (
            <div style={{ position: 'relative' }} data-dropdown-container>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                <span style={{ fontSize: 14 }}>{form.payment_account_type === 'bank' ? t('equipmentExpenses.add.bankLabel') : t('equipmentExpenses.add.safeLabel')}</span>
                <div data-dropdown-toggle onClick={() => setAccountDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  <span>
                    {getSelectedAccount()
                      ? (isRTL
                        ? getSelectedAccount().name_ar || getSelectedAccount().name_en || `#${form.payment_account_id}`
                        : getSelectedAccount().name_en || getSelectedAccount().name_ar || `#${form.payment_account_id}`)
                      : form.payment_account_type === 'bank' ? t('equipmentExpenses.add.selectBank') : t('equipmentExpenses.add.selectSafe')}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <DropdownWithSearch
                  isOpen={accountDropdownOpen}
                  searchTerm={accountSearchTerm}
                  onSearchChange={setAccountSearchTerm}
                  searchInputRef={accountSearchInputRef}
                  items={getAccountOptions()}
                  pagination={getAccountPagination()}
                  onPageChange={setAccountPage}
                  onClose={() => setAccountDropdownOpen(false)}
                  onSelect={(item) => {
                    setForm((prev) => ({ ...prev, payment_account_id: String(item.id) }));
                    setAccountDropdownOpen(false);
                    setAccountSearchTerm('');
                    setAccountPage(1);
                  }}
                  label={form.payment_account_type === 'bank' ? t('equipmentExpenses.add.bank') : t('equipmentExpenses.add.safe')}
                />
              </label>
            </div>
          )}

          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('equipmentExpenses.add.project')}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  data-dropdown-toggle
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setProjectDropdownOpen((prev) => !prev);
                  }}
                  style={{ flex: 1, ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                >
                  <span>
                    {form.project_id
                      ? (isRTL
                        ? projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_ar ||
                        projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_en ||
                        `${t('equipmentExpenses.tableHeaders.project')} #${form.project_id}`
                        : projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_en ||
                        projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_ar ||
                        `${t('equipmentExpenses.tableHeaders.project')} #${form.project_id}`)
                      : t('equipmentExpenses.add.selectProject')}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAddProjectModal(true);
                  }}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '44px',
                    height: '44px'
                  }}
                  title={t('equipmentExpenses.modals.addProject.title')}
                >
                  <FaPlus />
                </button>
              </div>
              <DropdownWithSearch
                isOpen={projectDropdownOpen}
                searchTerm={projectSearchTerm}
                onSearchChange={setProjectSearchTerm}
                searchInputRef={projectSearchInputRef}
                items={projectOptions}
                pagination={projectPagination}
                onPageChange={setProjectPage}
                onClose={() => setProjectDropdownOpen(false)}
                onSelect={(item) => {
                  setForm((prev) => ({ ...prev, project_id: String(item.id) }));
                  setProjectDropdownOpen(false);
                  setProjectSearchTerm('');
                  setProjectPage(1);
                }}
                label={t('equipmentExpenses.tableHeaders.project')}
              />
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('equipmentExpenses.add.notes')}</span>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={4}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                resize: 'vertical',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </label>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
            <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
              {isSubmitting ? t('equipmentExpenses.add.saving') : t('equipmentExpenses.add.save')}
            </button>
            <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/equipment-expenses')}>
              {t('equipmentExpenses.add.cancel')}
            </button>
          </div>
        </form>
      </div>

      {/* Add Equipment Modal */}
      {showAddEquipmentModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowAddEquipmentModal(false);
            setEquipmentForm({
              name_ar: '',
              name_en: '',
              code: '',
              is_active: true,
              notes: '',
            });
          }}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addEquipment.title')}</h2>
              <button
                onClick={() => {
                  setShowAddEquipmentModal(false);
                  setEquipmentForm({
                    name_ar: '',
                    name_en: '',
                    code: '',
                    is_active: true,
                    notes: '',
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddEquipment}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: "10px" }}>
                {isRTL ? (
                  <>
                    <Input label={t('equipmentExpenses.modals.addEquipment.nameAr')} name="name_ar" value={equipmentForm.name_ar} onChange={handleEquipmentFormChange} required isRTL={true} />
                    <Input label={t('equipmentExpenses.modals.addEquipment.nameEn')} name="name_en" value={equipmentForm.name_en} onChange={handleEquipmentFormChange} required isRTL={false} />
                  </>
                ) : (
                  <>
                    <Input label={t('equipmentExpenses.modals.addEquipment.nameEn')} name="name_en" value={equipmentForm.name_en} onChange={handleEquipmentFormChange} required isRTL={false} />
                    <Input label={t('equipmentExpenses.modals.addEquipment.nameAr')} name="name_ar" value={equipmentForm.name_ar} onChange={handleEquipmentFormChange} required isRTL={true} />
                  </>
                )}
              </div>
              <Input label={t('equipmentExpenses.modals.addEquipment.code')} name="code" value={equipmentForm.code} onChange={handleEquipmentFormChange} isRTL={isRTL} />
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '18px 0', }}>
                <span style={{ color: 'white' }}>{t('equipmentExpenses.modals.addEquipment.notes')}</span>
                <textarea
                  name="notes"
                  value={equipmentForm.notes}
                  onChange={handleEquipmentFormChange}
                  rows={4}
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    border: '1px solid #333',
                    backgroundColor: '#1a1f2e',
                    color: 'white',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left',
                  }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', color: "white", gap: 8, direction: isRTL ? 'rtl' : 'ltr' }}>
                <input type="checkbox" name="is_active" checked={equipmentForm.is_active} onChange={handleEquipmentFormChange} />
                {t('equipmentExpenses.modals.addEquipment.active')}
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button type="button" onClick={() => {
                  setShowAddEquipmentModal(false);
                  setEquipmentForm({
                    name_ar: '',
                    name_en: '',
                    code: '',
                    is_active: true,
                    notes: '',
                  });
                }} style={secondaryButtonStyle}>
                  {t('equipmentExpenses.modals.addEquipment.cancel')}
                </button>
                <button type="submit" disabled={isSubmittingEquipment} style={{ ...submitButtonStyle, opacity: isSubmittingEquipment ? 0.7 : 1, cursor: isSubmittingEquipment ? 'not-allowed' : 'pointer' }}>
                  {isSubmittingEquipment ? t('equipmentExpenses.modals.addEquipment.saving') : t('equipmentExpenses.modals.addEquipment.save')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Project Modal */}
      {showAddProjectModal && createPortal(
        <div
          data-modal
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowAddProjectModal(false);
            setProjectForm({
              code: '',
              name_ar: '',
              name_en: '',
              customer_id: null,
              start_date: '',
              end_date: '',
              status: 'planned',
              contract_value: '',
              tax_id: null,
              cost_center_id: null,
              notes: '',
            });
          }}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.title')}</h2>
              <button
                onClick={() => {
                  setShowAddProjectModal(false);
                  setProjectForm({
                    code: '',
                    name_ar: '',
                    name_en: '',
                    customer_id: null,
                    start_date: '',
                    end_date: '',
                    status: 'planned',
                    contract_value: '',
                    tax_id: null,
                    cost_center_id: null,
                    notes: '',
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddProject}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.code')}</label>
                  <input type="text" name="code" value={projectForm.code} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.nameAr')} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_ar" value={projectForm.name_ar} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.nameEn')}</label>
                  <input type="text" name="name_en" value={projectForm.name_en} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.startDate')}</label>
                  <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.endDate')}</label>
                  <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.status')}</label>
                  <select name="status" value={projectForm.status} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="planned">{t('equipmentExpenses.modals.addProject.statusOptions.planned')}</option>
                    <option value="in_progress">{t('equipmentExpenses.modals.addProject.statusOptions.inProgress')}</option>
                    <option value="paused">{t('equipmentExpenses.modals.addProject.statusOptions.paused')}</option>
                    <option value="completed">{t('equipmentExpenses.modals.addProject.statusOptions.completed')}</option>
                    <option value="cancelled">{t('equipmentExpenses.modals.addProject.statusOptions.cancelled')}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.contractValue')}</label>
                  <input type="number"  name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.customer', { defaultValue: 'العميل' })}</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div data-dropdown-toggle onClick={() => { setCustomerDropdownOpen((p) => !p); if (customerDropdownOpen) setCustomerSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
                      <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItem((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name') : t('equipmentExpenses.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('equipmentExpenses.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}</span>
                      <span style={{ fontSize: 12 }}>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddCustomerModal(true)}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '44px',
                        height: '44px'
                      }}
                      title={t('equipmentExpenses.modals.addProject.addCustomer', { defaultValue: 'إضافة عميل جديد' })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <DropdownWithSearch isOpen={customerDropdownOpen} searchTerm={customerSearchTerm} onSearchChange={setCustomerSearchTerm} searchInputRef={customerSearchInputRef} items={customers || []} pagination={customersPagination} onPageChange={setCustomerCurrentPage} onClose={() => setCustomerDropdownOpen(false)} onSelect={(item) => { setProjectForm((prev) => ({ ...prev, customer_id: item.id })); setCustomerDropdownOpen(false); setCustomerSearchTerm(''); setCustomerCurrentPage(1); }} label={t('equipmentExpenses.modals.addProject.customer', { defaultValue: 'عميل' })} />
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.tax', { defaultValue: 'الضريبة' })}</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <div data-dropdown-toggle onClick={() => { setTaxDropdownOpen((p) => !p); if (taxDropdownOpen) setTaxSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }}>
                      <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.tax_id ? (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)) ? (isRTL ? ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || '') : ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || '')) : t('equipmentExpenses.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' }) : t('equipmentExpenses.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                      <span style={{ fontSize: 12 }}>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddTaxModal(true)}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '44px',
                        height: '44px'
                      }}
                      title={t('equipmentExpenses.modals.addProject.addTax', { defaultValue: 'إضافة ضريبة جديدة' })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <DropdownWithSearch isOpen={taxDropdownOpen} searchTerm={taxSearchTerm} onSearchChange={setTaxSearchTerm} searchInputRef={taxSearchInputRef} items={taxes || []} pagination={taxesPagination} onPageChange={setTaxCurrentPage} onClose={() => setTaxDropdownOpen(false)} onSelect={(item) => { setProjectForm((prev) => ({ ...prev, tax_id: item.id })); setTaxDropdownOpen(false); setTaxSearchTerm(''); setTaxCurrentPage(1); }} label={t('equipmentExpenses.modals.addProject.tax', { defaultValue: 'ضريبة' })} />
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <div data-dropdown-toggle onClick={() => { setCostCenterDropdownOpen((p) => !p); if (costCenterDropdownOpen) setCostCenterSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }}>
                      <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.cost_center_id ? (costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)) ? renderDropdownItem((costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)), 'code', 'name_ar', 'name') : t('equipmentExpenses.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' }) : t('equipmentExpenses.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}</span>
                      <span style={{ fontSize: 12 }}>▼</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddCostCenterModal(true)}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '44px',
                        height: '44px'
                      }}
                      title={t('equipmentExpenses.modals.addProject.addCostCenter', { defaultValue: 'إضافة مركز تكلفة جديد' })}
                    >
                      <FaPlus />
                    </button>
                  </div>
                  <DropdownWithSearch isOpen={costCenterDropdownOpen} searchTerm={costCenterSearchTerm} onSearchChange={setCostCenterSearchTerm} searchInputRef={costCenterSearchInputRef} items={costCenters || []} pagination={costCentersPagination} onPageChange={setCostCenterCurrentPage} onClose={() => setCostCenterDropdownOpen(false)} onSelect={(item) => { setProjectForm((prev) => ({ ...prev, cost_center_id: item.id })); setCostCenterDropdownOpen(false); setCostCenterSearchTerm(''); setCostCenterCurrentPage(1); }} label={t('equipmentExpenses.modals.addProject.costCenter', { defaultValue: 'مركز تكلفة' })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.modals.addProject.notes')}</label>
                  <textarea name="notes" value={projectForm.notes} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button type="button" onClick={() => {
                  setShowAddProjectModal(false);
                  setProjectForm({
                    code: '',
                    name_ar: '',
                    name_en: '',
                    customer_id: null,
                    start_date: '',
                    end_date: '',
                    status: 'planned',
                    contract_value: '',
                    tax_id: null,
                    cost_center_id: null,
                    notes: '',
                  });
                }} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>
                  {t('equipmentExpenses.modals.addProject.cancel')}
                </button>
                <button type="submit" disabled={isSubmittingProject} style={{ backgroundColor: isSubmittingProject ? '#666' : '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: isSubmittingProject ? 'not-allowed' : 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}>
                  {isSubmittingProject ? t('equipmentExpenses.modals.addProject.saving') : t('equipmentExpenses.modals.addProject.save')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && createPortal(
        <div
          data-modal
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowAddCustomerModal(false);
            setCustomerForm({
              name_ar: '',
              name_en: '',
              code: '',
              contact_person: '',
              email: '',
              phone: '',
              address: '',
              tax_number: '',
              is_active: true,
              notes: '',
            });
          }}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.title', { defaultValue: 'إضافة عميل جديد' })}</h2>
              <button
                onClick={() => {
                  setShowAddCustomerModal(false);
                  setCustomerForm({
                    name_ar: '',
                    name_en: '',
                    code: '',
                    contact_person: '',
                    email: '',
                    phone: '',
                    address: '',
                    tax_number: '',
                    is_active: true,
                    notes: '',
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={customerForm.name_en}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={customerForm.name_ar}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.code', { defaultValue: 'الكود' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={customerForm.code}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={customerForm.contact_person}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.email', { defaultValue: 'البريد الإلكتروني' })}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.phone', { defaultValue: 'الهاتف' })}
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={customerForm.phone}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.address', { defaultValue: 'العنوان' })}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={customerForm.address}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.taxNumber', { defaultValue: 'الرقم الضريبي' })}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={customerForm.tax_number}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={customerForm.is_active}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCustomer.notes', { defaultValue: 'ملاحظات' })}
                  </label>
                  <textarea
                    name="notes"
                    value={customerForm.notes}
                    onChange={handleCustomerFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      resize: 'vertical',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCustomerModal(false);
                    setCustomerForm({
                      name_ar: '',
                      name_en: '',
                      code: '',
                      contact_person: '',
                      email: '',
                      phone: '',
                      address: '',
                      tax_number: '',
                      is_active: true,
                      notes: '',
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('quotations.add.modals.addCustomer.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCustomer}
                  style={{
                    backgroundColor: isSubmittingCustomer ? '#666' : '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer',
                    opacity: isSubmittingCustomer ? 0.7 : 1
                  }}
                >
                  {isSubmittingCustomer ? t('quotations.add.modals.addCustomer.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addCustomer.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Tax Modal */}
      {showAddTaxModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => {
            setShowAddTaxModal(false);
            setTaxForm({
              name_ar: '',
              name_en: '',
              rate: '',
              is_active: true,
            });
          }}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}</h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddTaxModal(false);
                  setTaxForm({
                    name_ar: '',
                    name_en: '',
                    rate: '',
                    is_active: true,
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={taxForm.name_ar}
                    onChange={handleTaxFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={taxForm.name_en}
                    onChange={handleTaxFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة %' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rate"
                    
                    value={taxForm.rate}
                    onChange={handleTaxFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={taxForm.is_active}
                    onChange={handleTaxFormChange}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTaxModal(false);
                    setTaxForm({
                      name_ar: '',
                      name_en: '',
                      rate: '',
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('quotations.add.modals.addTax.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingTax}
                  style={{
                    backgroundColor: isSubmittingTax ? '#666' : '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isSubmittingTax ? 'not-allowed' : 'pointer',
                    opacity: isSubmittingTax ? 0.7 : 1
                  }}
                >
                  {isSubmittingTax ? t('quotations.add.modals.addTax.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addTax.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Cost Center Modal */}
      {showAddCostCenterModal && createPortal(
        <div
          data-modal
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowAddCostCenterModal(false);
            setCostCenterForm({
              name_ar: '',
              name_en: '',
              code: '',
            });
          }}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCostCenter.title', { defaultValue: 'إضافة مركز تكلفة جديد' })}</h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddCostCenterModal(false);
                  setCostCenterForm({
                    name_ar: '',
                    name_en: '',
                    code: '',
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCostCenter}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={costCenterForm.name_ar}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={costCenterForm.name_en}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addCostCenter.code', { defaultValue: 'الكود' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={costCenterForm.code}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCostCenterModal(false);
                    setCostCenterForm({
                      name_ar: '',
                      name_en: '',
                      code: '',
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('quotations.add.modals.addCostCenter.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCostCenter}
                  style={{
                    backgroundColor: isSubmittingCostCenter ? '#666' : '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isSubmittingCostCenter ? 'not-allowed' : 'pointer',
                    opacity: isSubmittingCostCenter ? 0.7 : 1
                  }}
                >
                  {isSubmittingCostCenter ? t('quotations.add.modals.addCostCenter.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addCostCenter.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const Input = ({ label, isRTL, ...rest }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 14, color: 'white' }}>{label}</span>
    <input
      {...rest}
      style={{
        padding: 12,
        borderRadius: 8,
        border: '1px solid #333',
        backgroundColor: '#1a1f2e',
        color: 'white',
        direction: isRTL !== undefined ? (isRTL ? 'rtl' : 'ltr') : 'inherit',
        textAlign: isRTL !== undefined ? (isRTL ? 'right' : 'left') : 'inherit',
      }}
    />
  </label>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  boxSizing: 'border-box',
};

const cardStyle = {
  width: '100%',
  maxWidth: 'calc(100vw - 320px)', // Adjust based on sidebar width
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  boxSizing: 'border-box',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const filterInputStyle = {
  width: '100%',
  padding: 12,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const dropdownPaginationButtonStyle = (disabled) => ({
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: disabled ? '#333' : '#AC2000',
  color: 'white',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={dropdownMenuStyle} onClick={(e) => e.stopPropagation()} data-dropdown-menu>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={label === t('equipmentExpenses.tableHeaders.equipment')
            ? t('equipmentExpenses.dropdown.searchEquipment')
            : label === t('equipmentExpenses.tableHeaders.project')
              ? t('equipmentExpenses.dropdown.searchProject')
              : `Search for ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
              {t('equipmentExpenses.dropdown.page')} {pagination.current_page} {t('equipmentExpenses.dropdown.of')} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
            {t('equipmentExpenses.dropdown.noResults')}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{ padding: '10px 12px', borderTop: '1px solid #333', cursor: 'pointer', color: 'white', direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatEntity(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatEntity = (entity) => {
  if (!entity) return '—';
  const code = entity.code ? `${entity.code} - ` : '';
  const isRTL = i18n.language === 'ar';
  // Handle customers (may have 'name' instead of 'name_ar'/'name_en')
  if (entity.name && !entity.name_ar && !entity.name_en) {
    return `${code}${entity.name}` || `#${entity.id}`;
  }
  const name = isRTL
    ? (entity.name_ar || entity.name_en || entity.name || '')
    : (entity.name_en || entity.name_ar || entity.name || '');
  return `${code}${name}` || `#${entity.id}`;
};

export default AddEquipmentExpenses;
